// BOSS FORMS (owner 2026-11-02: "bosses that transform mid-fight", approved for ALL bosses). Pure + deterministic.
//
// Every boss (the map's final boss and the long-run guardians) has three FORMS. Crossing a health GATE (2/3 and 1/3 of max
// health) starts a TRANSFORMATION BEAT: the boss stops and braces, and for TRANSFORM_T seconds it cannot be hurt. At
// TRANSFORM_BURST its shell bursts: a shockwave throws nearby foes back, shoves the hero a few metres (no damage) and dents
// the ground; then the new form fights on with its own pace and attack list. Damage past a gate is lost (the boss cannot
// skip a form), except a lethal blow, which always kills (debug / QA one-shots, tests).
//
// The attack patterns are the Tyrant toolkit (charge, ring, summon) plus fan volleys, spirals, ground slams and telegraphed
// impact marks ("rain"). All randomness comes from the Sim's seeded Rng, all timing from fixed DT steps.
// The Sim owns the state (Enemy.form / Enemy.formT + a BossState per boss) and wires a BossHost; this file holds the data
// and the brain. Render / audio / HUD react to the SimEvents only (bossTransform, bossTele, bossSlam, bossMark, bossMarkHit).
import type { EnemyKind } from './content';
import { DT } from './skillStats';
import type { Rng } from './util';

/** health fractions where form 2 and form 3 begin */
export const FORM_GATES = [2 / 3, 1 / 3] as const;
/** transformation beat length (s): the boss cannot be hurt for this long, twice a fight */
export const TRANSFORM_T = 2.0;
/** seconds into the beat when the shell bursts (shockwave, new form) */
export const TRANSFORM_BURST = 1.3;
/** bosses with forms spawn with this share of their old health: the two beats (4 s untouchable) and the damage lost at the
 *  gates add back a little more than this takes away, so a fight runs about 5 % longer than before (measured over seeded
 *  fights with a fixed kit: 5:00 boss 46 s -> 49 s, 15:00 guardian 43 s -> 45 s; tests/bossForms.test.ts keeps it < +20 %) */
export const FORM_HP_MUL = 0.95;
/** burst shockwave: foes inside SHOCK_R are thrown back (impulse at the centre, falls off to 0 at the edge) */
export const SHOCK_R = 15, SHOCK_PUSH = 16, SHOCK_UP = 3;
/** burst shockwave: the hero is shoved up to SHOVE_DIST metres (no damage) when within SHOVE_R, over SHOVE_T seconds */
export const SHOVE_R = 16, SHOVE_DIST = 2.4, SHOVE_T = 0.3;
/** the dent the burst leaves in the ground (crater radius, m) */
export const FORM_CRATER_R = 5;
/** impact marks ("rain") never warn for less than this (the map hazards' crush rule) */
export const MARK_MIN_WARN = 1.3;

/** boss AI modes (Enemy.mode). 0-4 are the Tyrant's originals; 5-8 belong to the forms. Render reads them for wind-ups. */
export const BM = { chase: 0, chargeTele: 1, ring: 2, summon: 3, charging: 4, fan: 5, slam: 6, rain: 7, spiral: 8 } as const;

export type BossAtk =
  /** telegraphed straight charge; chain = charges in a row (re-aimed between), speed m/s */
  | { k: 'charge'; w: number; speed?: number; chain?: number }
  /** ring of shots; waves = rings in a row (each re-rolled), speed m/s */
  | { k: 'ring'; w: number; n: number; speed?: number; waves?: number; dmg?: number }
  /** a circle of foes around the boss */
  | { k: 'summon'; w: number; n: number; kinds: readonly EnemyKind[] }
  /** aimed fan of n shots over `spread` radians; waves = volleys in a row */
  | { k: 'fan'; w: number; n: number; spread: number; speed?: number; waves?: number; dmg?: number }
  /** rotating streams: `arms` shots every `every` s for `dur` s, the pattern turning `spin` rad/s */
  | { k: 'spiral'; w: number; arms: number; dur: number; every: number; spin: number; speed?: number; dmg?: number }
  /** braced ground slam: hurts the hero within r after `tele` s; ring = shots thrown out by the impact */
  | { k: 'slam'; w: number; r: number; dmg: number; tele?: number; ring?: number }
  /** n telegraphed impact marks (radius r) on and around the hero (within `spread` m), each landing after `warn` s */
  | { k: 'rain'; w: number; n: number; r: number; dmg: number; warn: number; spread: number };

export interface BossForm {
  /** shown on the boss bar after the boss name ("KILNHEART TITAN — MOLTEN HEART") and in the Encyclopedia */
  name: string;
  /** Encyclopedia line (player-facing) */
  desc: string;
  /** colour accent (render: glow, aura, transformation burst) */
  accent: number;
  /** chase speed multiplier, rest (time between attacks) multiplier, contact damage multiplier (boss-kind bodies only) */
  speed: number; rest: number; contact: number;
  attacks: readonly BossAtk[];
  /** guardians that keep their family AI (Ironhide Colossus, Prism Sovereign): seconds between overlay attacks */
  every?: number;
}
export interface BossFormSet { forms: readonly [BossForm, BossForm, BossForm] }

/** the Tyrant's original kit (form 1 of every boss keeps it exactly) */
const TYRANT_KIT: readonly BossAtk[] = [{ k: 'charge', w: 1 }, { k: 'ring', w: 1, n: 20 }, { k: 'summon', w: 1, n: 8, kinds: ['drone', 'skitter'] }];
const first = (name: string, desc: string, accent: number): BossForm => ({ name, desc, accent, speed: 1, rest: 1, contact: 1, attacks: TYRANT_KIT });

// ---------------------------------------------------------------------------------------------------------------------
// The forms (original designs; Docs/DESIGN/BOSS_FORMS.md has one paragraph per boss)
// ---------------------------------------------------------------------------------------------------------------------
export const BOSS_FORMS: Record<string, BossFormSet> = {
  'HOLLOW TYRANT': { forms: [
    first('HOLLOW TYRANT', 'A hunched colossus under a broken crown, its cracked core flickering in an open ribcage.', 0xff3ca8),
    { name: 'SHATTERED CROWN', desc: 'The crown fragments lock into a turning halo and the core blazes through the ribs. Charges twice in a row, fires double rings and aimed fans.',
      accent: 0xffc84a, speed: 1.12, rest: 0.85, contact: 1.1, attacks: [
        { k: 'charge', w: 3, chain: 2 }, { k: 'ring', w: 3, n: 22, waves: 2 }, { k: 'fan', w: 2, n: 7, spread: 0.9 }, { k: 'summon', w: 1, n: 8, kinds: ['drone', 'skitter'] }] },
    { name: 'HOLLOW STAR', desc: 'The ribcage splits open and the core rises above its shoulders as a burning star. Spins out spiral streams and calls crystals down from the sky.',
      accent: 0xff70f0, speed: 1.28, rest: 0.7, contact: 1.2, attacks: [
        { k: 'spiral', w: 3, arms: 3, dur: 2.4, every: 0.14, spin: 1.9 }, { k: 'rain', w: 2, n: 6, r: 3, dmg: 22, warn: 1.4, spread: 9 },
        { k: 'charge', w: 2, chain: 3, speed: 34 }, { k: 'ring', w: 2, n: 28 }] },
  ] },
  'TYRANT GUARDIAN': { forms: [
    first('TYRANT GUARDIAN', 'The Tyrant\'s sworn soldier behind a tower shield.', 0x3fe0d0),
    { name: 'SUNDERED SHIELD', desc: 'The tower shield cracks along its old scar and teal light pours out. Rams twice and slams the ground.',
      accent: 0x5cffe8, speed: 1.1, rest: 0.85, contact: 1.1, attacks: [
        { k: 'charge', w: 3, chain: 2 }, { k: 'slam', w: 3, r: 7.5, dmg: 24 }, { k: 'ring', w: 2, n: 20 }] },
    { name: 'OATHBREAKER', desc: 'It casts the broken shield aside and swings the blazing cleaver two-handed: quick fans of shots and a slam that throws a ring.',
      accent: 0xff5a7a, speed: 1.3, rest: 0.72, contact: 1.2, attacks: [
        { k: 'fan', w: 3, n: 5, spread: 0.6, waves: 3 }, { k: 'charge', w: 2, chain: 2, speed: 36 }, { k: 'slam', w: 2, r: 8, dmg: 26, ring: 12 }] },
  ] },
  // giant elites that keep their family AI: the forms add an overlay attack every few seconds
  'IRONHIDE COLOSSUS': { forms: [
    first('IRONHIDE COLOSSUS', 'A siege beast on pillar legs.', 0xff7a2a),
    { name: 'STOKED FURNACE', desc: 'Its furnace stacks roar white-hot; every few seconds it vents a ring of slag.', accent: 0xffb040, speed: 1.1, rest: 1, contact: 1, every: 6,
      attacks: [{ k: 'ring', w: 1, n: 16, speed: 11 }] },
    { name: 'SLAG BREACH', desc: 'Its armour splits over a molten core; slag rains down where you stand.', accent: 0xff5020, speed: 1.2, rest: 1, contact: 1, every: 4.5,
      attacks: [{ k: 'rain', w: 2, n: 5, r: 3, dmg: 20, warn: 1.4, spread: 8 }, { k: 'ring', w: 1, n: 20, speed: 11 }] },
  ] },
  'PRISM SOVEREIGN': { forms: [
    first('PRISM SOVEREIGN', 'A crystalline commander in a split mask.', 0x5fd8e8),
    { name: 'REFRACTED', desc: 'Its shoulder prisms split light into aimed fans of shards.', accent: 0x9ff0ff, speed: 1.1, rest: 1, contact: 1, every: 5,
      attacks: [{ k: 'fan', w: 1, n: 7, spread: 0.8 }] },
    { name: 'SPECTRUM CROWN', desc: 'A crown of prisms ignites over its mask: wide fans, rings and falling shards.', accent: 0xd890ff, speed: 1.2, rest: 1, contact: 1, every: 4,
      attacks: [{ k: 'fan', w: 2, n: 9, spread: 1.1 }, { k: 'ring', w: 1, n: 18 }, { k: 'rain', w: 1, n: 4, r: 3, dmg: 18, warn: 1.4, spread: 7 }] },
  ] },
  // map final bosses (src/maps/<id>/core.ts boss.name)
  'DUNEGLASS BEHEMOTH': { forms: [
    first('DUNEGLASS BEHEMOTH', 'A colossal sand burrower with a ridge of crystal blades.', 0x34cfc8),
    { name: 'GLASSHEART', desc: 'Its sand crust sloughs away to reveal a body of living glass around a burning crystal heart. Double rings of glass and wide fans.',
      accent: 0x8ffff2, speed: 1.1, rest: 0.85, contact: 1.1, attacks: [
        { k: 'ring', w: 3, n: 24, speed: 13, waves: 2 }, { k: 'fan', w: 2, n: 9, spread: 1.1 }, { k: 'charge', w: 2 }, { k: 'summon', w: 1, n: 8, kinds: ['skitter', 'splitter'] }] },
    { name: 'SHARDSTORM', desc: 'The glass body bursts into a storm of shards orbiting the heart: spiral streams and a hail of falling crystal.',
      accent: 0xd6a8ff, speed: 1.25, rest: 0.72, contact: 1.2, attacks: [
        { k: 'spiral', w: 3, arms: 4, dur: 2.2, every: 0.16, spin: 2.2 }, { k: 'rain', w: 3, n: 7, r: 3, dmg: 20, warn: 1.4, spread: 10 }, { k: 'charge', w: 2, chain: 2 }] },
  ] },
  'HOARSTONE KEEPER': { forms: [
    first('HOARSTONE KEEPER', 'The old boundary-stone guardian of the colonnade.', 0xa8f8ff),
    { name: 'RIMEWAKE', desc: 'Rime armour grows thick over the stone and the broken ring behind it lights up. Slams the ice to throw out a ring of frost.',
      accent: 0xc8f4ff, speed: 1.05, rest: 0.85, contact: 1.1, attacks: [
        { k: 'slam', w: 3, r: 8, dmg: 24, ring: 14 }, { k: 'ring', w: 2, n: 20, waves: 2 }, { k: 'charge', w: 2 }, { k: 'summon', w: 1, n: 8, kinds: ['drone', 'skitter'] }] },
    { name: 'WINTER HEART', desc: 'Its chest slabs fall away and the frozen heart rises into the stone ring as a cold star; icicles drop where you stand.',
      accent: 0xb890ff, speed: 1.2, rest: 0.72, contact: 1.2, attacks: [
        { k: 'rain', w: 3, n: 6, r: 3, dmg: 22, warn: 1.5, spread: 9 }, { k: 'fan', w: 2, n: 7, spread: 0.9 }, { k: 'spiral', w: 2, arms: 3, dur: 2.2, every: 0.15, spin: -1.8 }, { k: 'slam', w: 1, r: 8, dmg: 24 }] },
  ] },
  'CURFEW BASTION': { forms: [
    first('CURFEW BASTION', 'A walking tower block with lamp-eyes and an overpass across its shoulders.', 0xffae40),
    { name: 'LOCKDOWN', desc: 'Every window blazes and its lamp-eyes sweep like searchlights: volleys of aimed shots, double charges and a squad of Footmen.',
      accent: 0xff5aa8, speed: 1.1, rest: 0.85, contact: 1.1, attacks: [
        { k: 'fan', w: 3, n: 5, spread: 0.55, waves: 3 }, { k: 'charge', w: 2, chain: 2 }, { k: 'summon', w: 2, n: 10, kinds: ['drone'] }, { k: 'ring', w: 1, n: 20 }] },
    { name: 'ZERO HOUR', desc: 'The tower splits around its dial and the overpass deck breaks loose; slabs of road crash down around you.',
      accent: 0xff3040, speed: 1.2, rest: 0.72, contact: 1.2, attacks: [
        { k: 'rain', w: 3, n: 7, r: 3.2, dmg: 22, warn: 1.5, spread: 10 }, { k: 'slam', w: 2, r: 8, dmg: 26, ring: 16 }, { k: 'charge', w: 2, chain: 2 }, { k: 'ring', w: 1, n: 24 }] },
  ] },
  'THRUMBACK BEHEMOTH': { forms: [
    first('THRUMBACK BEHEMOTH', 'A house-sized grazer carrying a humming crystal grove.', 0x9a6cff),
    { name: 'RESONANT', desc: 'Its crystal grove surges taller and a third ring starts to hum: rings pulse out in threes and the ground thrums.',
      accent: 0x7af4ff, speed: 1.1, rest: 0.85, contact: 1.1, attacks: [
        { k: 'ring', w: 3, n: 18, waves: 3 }, { k: 'slam', w: 2, r: 7, dmg: 22 }, { k: 'charge', w: 2 }, { k: 'summon', w: 1, n: 8, kinds: ['wisp', 'skitter'] }] },
    { name: 'GEODE BLOOM', desc: 'The geode shell splits open and the singing heart floats free inside spinning rings: spirals, fans and falling crystals.',
      accent: 0xff8af0, speed: 1.25, rest: 0.72, contact: 1.2, attacks: [
        { k: 'spiral', w: 3, arms: 3, dur: 2.4, every: 0.14, spin: 1.6 }, { k: 'rain', w: 2, n: 6, r: 3, dmg: 20, warn: 1.4, spread: 9 }, { k: 'fan', w: 2, n: 7, spread: 0.9 }, { k: 'charge', w: 1, chain: 2 }] },
  ] },
  'CLOUDHULL LEVIATHAN': { forms: [
    first('CLOUDHULL LEVIATHAN', 'A floating stone sky-whale carrying a little isle.', 0x8ff0ff),
    { name: 'STORMHULL', desc: 'Its cloud skirt darkens into a thunderhead and the rune bands blaze: gale-breath fans and diving double charges.',
      accent: 0xffe070, speed: 1.15, rest: 0.85, contact: 1.1, attacks: [
        { k: 'fan', w: 3, n: 7, spread: 0.9, waves: 2 }, { k: 'charge', w: 3, chain: 2 }, { k: 'ring', w: 2, n: 22 }] },
    { name: 'BROKEN SKY', desc: 'The isle on its back tears loose and circles it as a ring of floating rocks, which it drops on you.',
      accent: 0x6cc2ff, speed: 1.25, rest: 0.72, contact: 1.2, attacks: [
        { k: 'rain', w: 3, n: 7, r: 3.2, dmg: 22, warn: 1.5, spread: 10 }, { k: 'spiral', w: 2, arms: 3, dur: 2.2, every: 0.15, spin: 2.0 }, { k: 'charge', w: 2, chain: 2, speed: 34 }] },
  ] },
  'KILNHEART TITAN': { forms: [
    first('KILNHEART TITAN', 'A basalt colossus with a furnace for a heart.', 0xff6a1a),
    { name: 'MOLTEN HEART', desc: 'Its chest splits wide and the furnace heart rises out of it: magma slams that throw rings of embers, and lava drops.',
      accent: 0xffc040, speed: 1.05, rest: 0.85, contact: 1.1, attacks: [
        { k: 'slam', w: 3, r: 8, dmg: 26, ring: 14 }, { k: 'rain', w: 2, n: 5, r: 3, dmg: 20, warn: 1.4, spread: 8 }, { k: 'ring', w: 2, n: 24 }, { k: 'charge', w: 1 }] },
    { name: 'MAGMA COLOSSUS', desc: 'The basalt shell melts away into a giant of living magma under a crown of flame: ember spirals, lava rain and double charges.',
      accent: 0xfff0a0, speed: 1.2, rest: 0.72, contact: 1.2, attacks: [
        { k: 'rain', w: 3, n: 7, r: 3, dmg: 22, warn: 1.4, spread: 10 }, { k: 'spiral', w: 2, arms: 4, dur: 2.2, every: 0.16, spin: -2.0 }, { k: 'charge', w: 2, chain: 2 }, { k: 'slam', w: 1, r: 8, dmg: 26 }] },
  ] },
  'OREHEART BEHEMOTH': { forms: [
    first('OREHEART BEHEMOTH', 'A canyon wall that learned to walk.', 0xff8a2a),
    { name: 'ORE-SPLIT', desc: 'The strata split along their seams and iron-ore crystals erupt across its back: rockslide slams and fans of ore shrapnel.',
      accent: 0xffd27a, speed: 1.1, rest: 0.85, contact: 1.1, attacks: [
        { k: 'slam', w: 3, r: 7.5, dmg: 24, ring: 12 }, { k: 'fan', w: 2, n: 7, spread: 0.9 }, { k: 'charge', w: 2 }, { k: 'summon', w: 1, n: 8, kinds: ['brute', 'drone'] }] },
    { name: 'MOTHERLODE', desc: 'The sandstone shell crashes off, leaving a molten iron giant studded with ore spikes that brings boulders down from the sky.',
      accent: 0xff4a20, speed: 1.25, rest: 0.72, contact: 1.2, attacks: [
        { k: 'rain', w: 3, n: 7, r: 3.2, dmg: 22, warn: 1.5, spread: 10 }, { k: 'charge', w: 2, chain: 3 }, { k: 'ring', w: 2, n: 26 }, { k: 'slam', w: 1, r: 8, dmg: 26 }] },
  ] },
};

/** forms of a boss by encounter name (unknown names: the Tyrant's set under generic names) */
export function formsFor(name: string): BossFormSet {
  const s = BOSS_FORMS[name]; if (s) return s;
  const t = BOSS_FORMS['HOLLOW TYRANT'].forms;
  return { forms: [{ ...t[0], name }, { ...t[1], name: 'AWAKENED' }, { ...t[2], name: 'UNBOUND' }] };
}
/** health at which form f (0-based) ends: the next transformation starts when health would drop to it (-1 = final form) */
export const gateHp = (form: number, maxHp: number): number => (form < FORM_GATES.length ? maxHp * FORM_GATES[form] : -1);
/** the damage a boss may take right now from a blow of d. While transforming: none. A lethal blow always lands in full.
 *  A blow that crosses the next gate is cut at the gate and `cross` is true (the caller starts the transformation). */
export function gateDamage(hp: number, maxHp: number, form: number, formT: number, d: number): { d: number; cross: boolean } {
  if (formT > 0) return { d: 0, cross: false };
  if (d >= hp) return { d, cross: false };
  const g = gateHp(form, maxHp);
  if (g < 0 || hp - d > g) return { d, cross: false };
  return { d: Math.max(0, hp - g), cross: true };
}

// ---------------------------------------------------------------------------------------------------------------------
// Brain
// ---------------------------------------------------------------------------------------------------------------------
/** per-boss form state (the Sim keeps one per boss with forms, by enemy id) */
export interface BossState {
  name: string; set: BossFormSet;
  /** seconds into the transformation beat; burst done */
  tt: number; burst: boolean;
  /** current attack, volleys / charges left, spiral angle and shot clock */
  atk: BossAtk | null; left: number; spin: number; acc: number;
  /** overlay guardians: seconds to the next attack, telegraph seconds left, the telegraphed attack */
  ot: number; otele: number; oatk: BossAtk | null;
}
/** an impact mark ("rain"): lands at (x, z) when t reaches warn */
export interface BossMark { id: number; x: number; z: number; r: number; warn: number; t: number; dmg: number; color: number }

/** the boss's body as the brain sees it (a structural subset of the Sim's Enemy) */
export interface BossBody {
  x: number; y: number; z: number; vx: number; vz: number; kx: number; kz: number;
  mode: number; modeT: number; dirX: number; dirZ: number; pinT: number; stunT: number;
  form: number; formT: number; id: number; def: { speed: number };
}
export type BossEvent =
  | { t: 'bossTele'; kind: 'charge' | 'ring' | 'summon' | 'fan' | 'spiral' | 'slam' | 'rain' }
  /** a boss transforms: start = the beat begins (form = the form it is becoming), burst = the shell bursts and the new form
   *  takes over (shockwave radius r), end = it fights again. boss = encounter name, name = the new form's name */
  | { t: 'bossTransform'; phase: 'start' | 'burst' | 'end'; form: number; x: number; z: number; r: number; boss: string; name: string; accent: number }
  /** a braced slam lands at (x, z) with radius r */
  | { t: 'bossSlam'; x: number; z: number; r: number; color: number }
  /** an impact mark appears (lands after warn s) / lands */
  | { t: 'bossMark'; id: number; x: number; z: number; r: number; warn: number; color: number }
  | { t: 'bossMarkHit'; id: number; x: number; z: number; r: number; color: number }
  | { t: 'explode'; x: number; y: number; z: number; r: number; color: number };

/** what the brain may do to the world (wired by the Sim to its private helpers) */
export interface BossHost {
  rng: Rng; tick: number; time: number;
  /** chase speed factor from the Sim (enemy speed tuning, the run pace, the Stillness slow) */
  speedK: number;
  /** run pace (src/core/mods.ts): shots and charges travel pace x as fast (same reach: the Sim's hostile shots live 1 / pace
   *  as long), wind-ups / telegraphs last 1 / pace as long. Cooldowns, beat lengths, damage and areas are unpaced. */
  pace: number;
  player: { x: number; z: number };
  events: { push(e: BossEvent): unknown };
  marks: BossMark[];
  fire(x: number, y: number, z: number, vx: number, vz: number, dmg: number): void;
  spawn(kind: EnemyKind, x: number, z: number): void;
  hurt(dmg: number): void;
  /** the burst: knock foes back, shove the hero, dent the ground, break props */
  shockwave(b: BossBody): void;
  /** slam / mark impact: dent the ground and break props at (x, z) */
  impact(x: number, z: number, r: number): void;
}

export function newBossState(name: string): BossState {
  return { name, set: formsFor(name), tt: 0, burst: false, atk: null, left: 0, spin: 0, acc: 0, ot: 3, otele: 0, oatk: null };
}
export const formOf = (st: BossState, b: { form: number }): BossForm => st.set.forms[Math.max(0, Math.min(2, b.form))];

function pick(f: BossForm, rng: Rng): BossAtk {
  let tot = 0; for (const a of f.attacks) tot += a.w;
  let r = rng.next() * tot;
  for (const a of f.attacks) { r -= a.w; if (r < 0) return a; }
  return f.attacks[f.attacks.length - 1];
}
const teleKind = (a: BossAtk): BossEvent & { t: 'bossTele' } => ({ t: 'bossTele', kind: a.k });

/** begin the transformation beat (call when gateDamage reported a crossing) */
export function startTransform(b: BossBody, st: BossState, h: BossHost): void {
  if (b.formT > 0 || b.form >= 2) return;
  st.tt = 0; st.burst = false; st.atk = null; st.oatk = null; st.otele = 0;
  b.formT = TRANSFORM_T; b.mode = BM.chase; b.modeT = 0; b.pinT = 0; b.stunT = 0;
  const f = st.set.forms[b.form + 1];
  h.events.push({ t: 'bossTransform', phase: 'start', form: b.form + 1, x: b.x, z: b.z, r: SHOCK_R, boss: st.name, name: f.name, accent: f.accent });
}

/** one step of the transformation beat. The boss holds still, cannot be pinned, frozen or hurt. */
export function transformStep(b: BossBody, st: BossState, h: BossHost): void {
  st.tt += DT; b.formT = Math.max(1e-6, TRANSFORM_T - st.tt);
  b.vx *= 0.8; b.vz *= 0.8; b.kx = 0; b.kz = 0; b.pinT = 0; b.stunT = 0;
  b.x += b.vx * DT; b.z += b.vz * DT;
  if (!st.burst && st.tt >= TRANSFORM_BURST) {
    st.burst = true; b.form = Math.min(2, b.form + 1);
    const f = formOf(st, b);
    h.events.push({ t: 'bossTransform', phase: 'burst', form: b.form, x: b.x, z: b.z, r: SHOCK_R, boss: st.name, name: f.name, accent: f.accent });
    h.shockwave(b);
  }
  if (st.tt >= TRANSFORM_T - 1e-9) {
    b.formT = 0; b.mode = BM.chase; b.modeT = 1.0; st.atk = null; st.ot = 1.5;
    const f = formOf(st, b);
    h.events.push({ t: 'bossTransform', phase: 'end', form: b.form, x: b.x, z: b.z, r: SHOCK_R, boss: st.name, name: f.name, accent: f.accent });
  }
}

function rest(b: BossBody, st: BossState, f: BossForm, base: number): void { b.mode = BM.chase; b.modeT = base * f.rest; st.atk = null; }

function begin(b: BossBody, st: BossState, h: BossHost, a: BossAtk, nx: number, nz: number): void {
  st.atk = a; b.dirX = nx; b.dirZ = nz;
  switch (a.k) {
    // (pace: every wind-up lasts 1 / pace as long, as the Tyrant's in sim.ts)
    case 'charge': b.mode = BM.chargeTele; b.modeT = 0.9 / h.pace; st.left = a.chain ?? 1; break;
    case 'ring': b.mode = BM.ring; b.modeT = 0.6 / h.pace; st.left = a.waves ?? 1; break;
    case 'summon': b.mode = BM.summon; b.modeT = 0.7 / h.pace; break;
    case 'fan': b.mode = BM.fan; b.modeT = 0.7 / h.pace; st.left = a.waves ?? 1; break;
    case 'slam': b.mode = BM.slam; b.modeT = (a.tele ?? 1.0) / h.pace; break;
    case 'rain': b.mode = BM.rain; b.modeT = 0.6 / h.pace; break;
    case 'spiral': b.mode = BM.spiral; b.modeT = a.dur; st.acc = 0; st.spin = h.rng.next() * Math.PI * 2; break;
  }
  h.events.push(teleKind(a));
}

function ring(b: BossBody, h: BossHost, n: number, speed: number, dmg: number): void {
  const off = h.rng.next(); speed *= h.pace; // (pace: the Sim's hostile shots take paced speeds)
  for (let i = 0; i < n; i++) { const a = ((i + off) / n) * Math.PI * 2; h.fire(b.x, b.y, b.z, Math.cos(a) * speed, Math.sin(a) * speed, dmg); }
}
function fan(b: BossBody, h: BossHost, a: BossAtk & { k: 'fan' }, dx: number, dz: number): void {
  const base = Math.atan2(dz, dx), sp = (a.speed ?? 13) * h.pace, n = a.n;
  for (let i = 0; i < n; i++) { const t = n > 1 ? i / (n - 1) - 0.5 : 0, ang = base + t * a.spread; h.fire(b.x, b.y, b.z, Math.cos(ang) * sp, Math.sin(ang) * sp, a.dmg ?? 14); }
}
function rain(b: BossBody, h: BossHost, a: BossAtk & { k: 'rain' }, color: number): void {
  const p = h.player, warn = Math.max(MARK_MIN_WARN, a.warn / h.pace); // pace: a slower warning (never under the minimum)
  for (let i = 0; i < a.n; i++) {
    let x = p.x, z = p.z;
    if (i > 0) { const ang = h.rng.next() * Math.PI * 2, d = a.r * 1.2 + h.rng.next() * (a.spread - a.r * 1.2); x += Math.cos(ang) * d; z += Math.sin(ang) * d; }
    const id = (h.tick * 16 + i + b.id * 7) | 0, m: BossMark = { id, x, z, r: a.r, warn: warn + i * 0.12, t: 0, dmg: a.dmg, color };
    h.marks.push(m);
    h.events.push({ t: 'bossMark', id, x, z, r: a.r, warn: m.warn, color });
  }
}
/** execute an attack at once (overlay guardians; their telegraph ran on st.otele) */
function strike(b: BossBody, h: BossHost, a: BossAtk, nx: number, nz: number, accent: number): void {
  switch (a.k) {
    case 'ring': ring(b, h, a.n, a.speed ?? 12, a.dmg ?? 16); h.events.push({ t: 'explode', x: b.x, y: b.y, z: b.z, r: 4, color: 0xff3af0 }); break; // (hostile colour: the colour-blind palette remaps it)
    case 'fan': fan(b, h, a, nx, nz); break;
    case 'rain': rain(b, h, a, accent); break;
    case 'summon': for (let i = 0; i < a.n; i++) { const g = (i / a.n) * Math.PI * 2; h.spawn(a.kinds[i % a.kinds.length], b.x + Math.cos(g) * 4, b.z + Math.sin(g) * 4); } break;
    default: break; // charge / slam / spiral need the boss body's own modes (boss-kind only)
  }
}

/**
 * Attack selection + execution for a boss-kind body with forms (replaces the Tyrant's fixed switch; form 1 plays it
 * exactly). Call after modeT was counted down. (nx, nz) = unit direction to the hero.
 */
export function bossBrain(b: BossBody, st: BossState, h: BossHost, nx: number, nz: number): void {
  const f = formOf(st, b), a = st.atk;
  switch (b.mode) {
    case BM.chase: {
      const s = b.def.speed * f.speed * h.speedK;
      b.vx += (nx * s - b.vx) * 0.08; b.vz += (nz * s - b.vz) * 0.08;
      if (b.modeT <= 0) begin(b, st, h, pick(f, h.rng), nx, nz);
      break;
    }
    case BM.chargeTele:
      b.vx *= 0.85; b.vz *= 0.85; b.dirX = nx; b.dirZ = nz;
      if (b.modeT <= 0) { b.mode = BM.charging; b.modeT = 0.75 / h.pace; } // pace: same charge reach at pace x the speed
      break;
    case BM.charging: {
      const sp = (a && a.k === 'charge' ? a.speed ?? 30 : 30) * h.pace;
      b.vx = b.dirX * sp; b.vz = b.dirZ * sp;
      if (h.tick % 4 === 0) h.events.push({ t: 'explode', x: b.x, y: 0.2, z: b.z, r: 1.6, color: 0xff4060 });
      if (b.modeT <= 0) {
        if (--st.left > 0) { b.mode = BM.chargeTele; b.modeT = 0.45 / h.pace; h.events.push({ t: 'bossTele', kind: 'charge' }); }
        else rest(b, st, f, 4);
      }
      break;
    }
    case BM.ring:
      b.vx *= 0.9; b.vz *= 0.9;
      if (b.modeT <= 0) {
        const r = a && a.k === 'ring' ? a : { n: 20, speed: 12, dmg: 16 };
        ring(b, h, r.n, r.speed ?? 12, r.dmg ?? 16);
        h.events.push({ t: 'explode', x: b.x, y: b.y, z: b.z, r: 4, color: 0xff3af0 });
        if (--st.left > 0) b.modeT = 0.5; else rest(b, st, f, 3.6);
      }
      break;
    case BM.summon:
      b.vx *= 0.9; b.vz *= 0.9;
      if (b.modeT <= 0) {
        const s = a && a.k === 'summon' ? a : { n: 8, kinds: ['drone', 'skitter'] as const };
        for (let i = 0; i < s.n; i++) { const g = (i / s.n) * Math.PI * 2; h.spawn(s.kinds[i % s.kinds.length], b.x + Math.cos(g) * 4, b.z + Math.sin(g) * 4); }
        h.events.push({ t: 'explode', x: b.x, y: b.y, z: b.z, r: 5, color: 0xa040ff });
        rest(b, st, f, 3.5);
      }
      break;
    case BM.fan:
      b.vx *= 0.88; b.vz *= 0.88; b.dirX = nx; b.dirZ = nz;
      if (b.modeT <= 0) {
        if (a && a.k === 'fan') fan(b, h, a, nx, nz);
        if (--st.left > 0) b.modeT = 0.32; else rest(b, st, f, 3.2);
      }
      break;
    case BM.slam:
      b.vx *= 0.8; b.vz *= 0.8;
      if (b.modeT <= 0) {
        if (a && a.k === 'slam') {
          const p = h.player;
          if (Math.hypot(p.x - b.x, p.z - b.z) < a.r) h.hurt(a.dmg);
          h.events.push({ t: 'bossSlam', x: b.x, z: b.z, r: a.r, color: f.accent });
          h.impact(b.x, b.z, Math.min(6, a.r * 0.55));
          if (a.ring) ring(b, h, a.ring, 11, 14);
        }
        rest(b, st, f, 3.4);
      }
      break;
    case BM.rain:
      b.vx *= 0.85; b.vz *= 0.85;
      if (b.modeT <= 0) { if (a && a.k === 'rain') rain(b, h, a, f.accent); rest(b, st, f, 3.4); }
      break;
    case BM.spiral: {
      b.vx *= 0.9; b.vz *= 0.9;
      if (a && a.k === 'spiral') {
        st.spin += a.spin * DT; st.acc += DT;
        while (st.acc >= a.every) {
          st.acc -= a.every;
          const sp = (a.speed ?? 10) * h.pace;
          for (let k = 0; k < a.arms; k++) { const g = st.spin + (k / a.arms) * Math.PI * 2; h.fire(b.x, b.y, b.z, Math.cos(g) * sp, Math.sin(g) * sp, a.dmg ?? 10); }
        }
      }
      if (b.modeT <= 0) rest(b, st, f, 3.0);
      break;
    }
    default: b.mode = BM.chase; b.modeT = 1;
  }
}

/** guardians that keep their family AI: forms 2 and 3 add a telegraphed overlay attack every `every` s */
export function bossOverlay(b: BossBody, st: BossState, h: BossHost, nx: number, nz: number): void {
  const f = formOf(st, b); if (!f.every || !b.form) return;
  if (st.otele > 0) {
    st.otele -= DT;
    if (st.otele <= 0 && st.oatk) { strike(b, h, st.oatk, nx, nz, f.accent); st.oatk = null; }
    return;
  }
  st.ot -= DT;
  if (st.ot <= 0) { st.oatk = pick(f, h.rng); st.otele = 0.7 / h.pace; st.ot = f.every; h.events.push(teleKind(st.oatk)); }
}

/** impact marks land (call once per tick while any exist) */
export function updateMarks(h: BossHost): void {
  const M = h.marks, p = h.player;
  for (let i = M.length - 1; i >= 0; i--) {
    const m = M[i]; m.t += DT;
    if (m.t < m.warn) continue;
    if (Math.hypot(p.x - m.x, p.z - m.z) < m.r) h.hurt(m.dmg);
    h.events.push({ t: 'bossMarkHit', id: m.id, x: m.x, z: m.z, r: m.r, color: m.color });
    h.impact(m.x, m.z, m.r * 0.6);
    M.splice(i, 1);
  }
}
